import { useCallback, useEffect, useRef, useState } from "react";
import { api, parseApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from "@/components/ui/drawer";
import { ChefHat, Clock, CheckCheck, Camera, Check, Upload, X, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { compressImageToDataUrl, fileToCompressedDataUrl } from "@/lib/image";

const STATUS_COLOR = {
  pending: "bg-stone-200 text-stone-900",
  cooking: "bg-amber-100 text-amber-900",
  ready: "bg-emerald-100 text-emerald-900",
};
const NEXT = { pending: "cooking", cooking: "ready", ready: "served" };
const NEXT_LABEL = { pending: "🔥 Bắt đầu nấu", cooking: "✓ Đã sẵn sàng", ready: "✓ Đã phục vụ" };

const TASK_STATUS = {
  open: { l: "Mở", c: "bg-stone-200 text-stone-800" },
  pending: { l: "Mở", c: "bg-stone-200 text-stone-800" },
  in_progress: { l: "Đang làm", c: "bg-blue-100 text-blue-900" },
};
const TASK_OPEN = new Set(["open", "pending", "in_progress"]);

export default function StaffKDS() {
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState({});

  // --- Orders (existing KDS logic, unchanged) ---
  const load = () => api.get("/pos/kds").then((r) => setLines(r.data));
  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const advance = async (lid, current) => {
    setBusy((b) => ({ ...b, [lid]: true }));
    try {
      await api.post(`/pos/lines/${lid}/kds`, { status: NEXT[current] });
      toast.success(NEXT_LABEL[current]);
      load();
    } catch { toast.error("Lỗi"); }
    finally { setBusy((b) => ({ ...b, [lid]: false })); }
  };

  // --- Tasks tab (#99) ---
  const [tasks, setTasks] = useState([]);
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");
  const [photo, setPhoto] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const streamRef = useRef(null);
  const videoRef = useRef(null);

  const loadTasks = useCallback(() => {
    const today = new Date().toISOString().slice(0, 10);
    api.get(`/hrm/daily-work/my-tasks?work_date=${today}`)
      .then((r) => setTasks((r.data || []).filter((t) => TASK_OPEN.has(t.status))))
      .catch(() => {});
  }, []);
  useEffect(() => { loadTasks(); }, [loadTasks]);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraOpen(false);
  }, []);
  useEffect(() => () => stopCamera(), [stopCamera]);
  useEffect(() => {
    if (cameraOpen && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [cameraOpen]);

  const openTask = (t) => { stopCamera(); setPhoto(t.photo_url || null); setNote(t.completion_note || ""); setActive(t); };
  const closeTask = () => { stopCamera(); setActive(null); setNote(""); setPhoto(null); };

  const startCamera = async () => {
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      setCameraOpen(true);
    } catch { toast.error("Không mở được camera. Hãy thử tải ảnh lên."); }
  };
  const capturePhoto = () => { if (videoRef.current) { setPhoto(compressImageToDataUrl(videoRef.current)); stopCamera(); } };
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try { setPhoto(await fileToCompressedDataUrl(file)); } catch { toast.error("Không đọc được ảnh"); }
  };

  const completeTask = async () => {
    if (active.requires_photo && !photo) { toast.error("Cần chụp ảnh để hoàn thành"); return; }
    try {
      await api.post(`/hrm/daily-work/items/${active.id}/complete`, {
        status: "done",
        note,
        photo_url: photo || undefined,
        version: active.version,
      });
      toast.success(active.requires_photo ? "Đã gửi duyệt 🎉" : "Đã hoàn thành");
      closeTask(); loadTasks();
    } catch (e) { toast.error(parseApiError(e, "Thất bại").message); }
  };

  return (
    <div className="p-4 space-y-3" data-testid="staff-kds">
      <Tabs defaultValue="orders">
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="orders" data-testid="tab-orders">
            <ChefHat className="w-4 h-4 mr-1.5 text-amber-700" />Orders
          </TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks">
            <ListChecks className="w-4 h-4 mr-1.5 text-emerald-700" />Tasks{tasks.length > 0 ? ` (${tasks.length})` : ""}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-3">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-serif text-xl font-bold flex items-center gap-2"><ChefHat className="w-5 h-5 text-amber-700" />KDS Bếp</h2>
            <div className="text-xs text-stone-500 font-mono flex items-center gap-1"><Clock className="w-3 h-3" />Tự cập nhật 8s</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {lines.map((l) => {
              const elapsed = Math.floor((Date.now() - new Date(l.created_at).getTime()) / 60000);
              const urgent = elapsed > 15;
              return (
                <div key={l.id} className={`bg-white border rounded-sm p-3 ${urgent ? "border-red-400" : "border-amber-200"}`} data-testid={`kds-line-${l.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">{l.order_code} {l.table_no && `• Bàn ${l.table_no}`}</div>
                      <div className="font-bold text-stone-900">{l.sku_name}</div>
                      <div className="text-xs text-stone-500 font-mono">SL: {l.qty}</div>
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-bold ${STATUS_COLOR[l.kds_status]}`}>{l.kds_status}</span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-stone-100">
                    <div className={`text-xs font-mono ${urgent ? "text-red-700 font-bold" : "text-stone-500"}`}>⏱ {elapsed} phút</div>
                    {NEXT[l.kds_status] && (
                      <button
                        onClick={() => advance(l.id, l.kds_status)}
                        disabled={busy[l.id]}
                        className={`text-xs px-3 py-1.5 rounded-sm font-bold tap-target ${
                          l.kds_status === "cooking" ? "bg-emerald-700 text-white" :
                          l.kds_status === "ready" ? "bg-blue-700 text-white" :
                          "bg-amber-600 text-white"
                        }`}
                        data-testid={`kds-advance-${l.id}`}
                      >
                        {NEXT_LABEL[l.kds_status]}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {lines.length === 0 && (
              <div className="col-span-full text-center text-stone-400 py-12">
                <CheckCheck className="w-12 h-12 mx-auto mb-2 text-stone-300" />
                <p className="text-sm">Không có món chờ nấu. 🎉</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="tasks" className="mt-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {tasks.map((t) => {
              const st = TASK_STATUS[t.status] || { l: t.status, c: "bg-stone-100" };
              return (
                <button
                  key={t.id}
                  onClick={() => openTask(t)}
                  className="text-left bg-white border border-emerald-200 rounded-sm p-3 active:bg-emerald-50"
                  data-testid={`task-card-${t.id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="font-bold text-stone-900 flex items-center gap-1.5 min-w-0">
                      {t.requires_photo && <Camera className="w-3.5 h-3.5 text-amber-700 shrink-0" />}
                      <span className="truncate">{t.task_name}</span>
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-bold shrink-0 ${st.c}`}>{st.l}</span>
                  </div>
                  <div className="text-xs text-stone-500">{t.shift_name || "Ca"} · {t.branch_name || "Cơ sở"}</div>
                </button>
              );
            })}
            {tasks.length === 0 && (
              <div className="col-span-full text-center text-stone-400 py-12">
                <ListChecks className="w-12 h-12 mx-auto mb-2 text-stone-300" />
                <p className="text-sm">Không có việc nào đang chờ. 🎉</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <Drawer open={!!active} onOpenChange={(o) => !o && closeTask()}>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader className="text-left">
            <DrawerTitle className="font-serif text-xl">{active?.task_name}</DrawerTitle>
            {active?.expected_result && <p className="text-sm text-stone-600">{active.expected_result}</p>}
          </DrawerHeader>
          {active && (
            <div className="px-4 space-y-3 overflow-y-auto">
              {active.sop_steps?.length > 0 && (
                <ul className="space-y-1.5">
                  {active.sop_steps.map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <div className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-900 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</div>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              )}
              {active.requires_photo && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-amber-800 font-bold mb-1">Ảnh xác minh (bắt buộc)</div>
                  {cameraOpen ? (
                    <div className="space-y-2">
                      <video ref={videoRef} playsInline muted className="w-full rounded-sm bg-black aspect-video object-cover" />
                      <div className="grid grid-cols-2 gap-2">
                        <Button type="button" onClick={capturePhoto} className="h-11 bg-amber-700 hover:bg-amber-800 rounded-sm" data-testid="btn-capture"><Camera className="w-4 h-4 mr-2" />Chụp</Button>
                        <Button type="button" variant="outline" onClick={stopCamera} className="h-11 rounded-sm"><X className="w-4 h-4 mr-2" />Huỷ</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      {photo && (
                        <div className="relative shrink-0">
                          <img src={photo} alt="Ảnh xác minh" className="w-20 h-20 rounded-md object-cover border border-stone-200" data-testid="task-photo-preview" />
                          <button type="button" onClick={() => setPhoto(null)} className="absolute -top-1.5 -right-1.5 bg-white border border-stone-300 rounded-full p-0.5" aria-label="Xoá ảnh"><X className="w-3 h-3 text-stone-600" /></button>
                        </div>
                      )}
                      <div className="flex flex-col gap-2 flex-1">
                        <Button type="button" onClick={startCamera} className="h-11 bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-camera"><Camera className="w-4 h-4 mr-2" />Chụp ảnh</Button>
                        <label className="h-11 inline-flex items-center justify-center rounded-sm border border-stone-300 text-sm font-medium cursor-pointer active:bg-stone-50" data-testid="btn-upload">
                          <Upload className="w-4 h-4 mr-2" />Tải ảnh lên
                          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleUpload} />
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Ghi chú khi hoàn thành..." className="rounded-sm" data-testid="task-note" />
            </div>
          )}
          <DrawerFooter className="grid grid-cols-1 gap-2">
            <Button onClick={completeTask} className="h-12 bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-done">
              <Check className="w-4 h-4 mr-2" />{active?.requires_photo ? "Gửi duyệt" : "Đánh dấu xong"}
            </Button>
            <Button variant="outline" onClick={closeTask} className="rounded-sm h-10">Đóng</Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
